
import React, { useState, useEffect } from 'react';
import { Mail, Lock, User, ArrowLeft, Loader2, CheckCircle2, AlertTriangle, Phone, Building2 } from 'lucide-react';
import { supabase } from '../supabase';
import { UserRole } from '../types';

interface RegisterProps {
  onSwitchToLogin: () => void;
}

const Register: React.FC<RegisterProps> = ({ onSwitchToLogin }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [targetCabinetId, setTargetCabinetId] = useState<string | null>(null);
  const [targetCabinetName, setTargetCabinetName] = useState<string>('');
  const [inviteType, setInviteType] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cabinetSlug = params.get('cabinet');
    const cabinetId = params.get('cabinet_id');
    const type = params.get('type');

    if (cabinetSlug) {
       resolveSlugToId(cabinetSlug);
       setInviteType(type);
    } else if (cabinetId) {
       setTargetCabinetId(cabinetId);
       setInviteType(type);
       fetchCabinetName(cabinetId);
    }
  }, []);

  const resolveSlugToId = async (slug: string) => {
     try {
        const { data } = await supabase.from('containers').select('id, name').eq('slug', slug).single();
        if (data) {
           setTargetCabinetId(data.id);
           setTargetCabinetName(data.name);
        }
     } catch (e) {
        console.error("Erro ao resolver slug do gabinete");
     }
  };

  const fetchCabinetName = async (id: string) => {
    try {
      const { data } = await supabase.from('containers').select('name').eq('id', id).single();
      if (data) setTargetCabinetName(data.name);
    } catch (e) {
      console.error("Erro ao buscar nome do gabinete");
    }
  };

  const formatPhone = (val: string) => {
    let v = val.replace(/\D/g, '').slice(0, 11);
    if (v.length > 2) v = `(${v.slice(0, 2)}) ${v.slice(2)}`;
    if (v.length > 10) v = `${v.slice(0, 10)}-${v.slice(10)}`;
    setPhone(v);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg(null);

    try {
      const role = inviteType === 'leadership' ? UserRole.LEADERSHIP : UserRole.EDITOR;

      const { data, error: authError } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          data: {
            full_name: name,
            phone: phone,
            role: role 
          }
        }
      });
      
      if (authError) throw authError;

      if (data.user) {
        if (data.user.identities && data.user.identities.length === 0) {
            setErrorMsg("Este e-mail já está cadastrado.");
            setIsSubmitting(false);
            return;
        }

        const permissions = {
          canEditDocuments: role !== UserRole.LEADERSHIP,
          canEditAttendances: true,
          canEditCRM: true,
          canEditTasks: role !== UserRole.LEADERSHIP,
          canEditOwnContent: role === UserRole.LEADERSHIP
        };

        await supabase.from('profiles').upsert({
          id: data.user.id,
          name: name.trim(),
          email: email.toLowerCase().trim(),
          phone: phone.replace(/\D/g, ''),
          role: role,
          container_id: null,
          permissions: permissions
        }, { onConflict: 'id' });

        if (targetCabinetId) {
            await supabase.from('invitations').insert({
                email: email.toLowerCase().trim(),
                role: role,
                container_id: targetCabinetId,
                status: 'request'
            });
        }

        setSuccess(true);
      }
    } catch (err: any) {
      console.error(err);
      let msg = err.message || "Erro desconhecido.";
      if (msg.includes("Database error") || msg.includes("violates") || msg.includes("security policy")) {
        msg = "Erro de sistema. Entre em contato com o administrador.";
      }
      setErrorMsg(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white dark:bg-navy-950 flex items-center justify-center p-6">
        <div className="w-full max-w-[420px] bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-[32px] p-10 text-center space-y-6 shadow-2xl animate-in zoom-in-95">
          <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-full flex items-center justify-center mx-auto"><CheckCircle2 size={40} /></div>
          <h2 className="text-3xl font-black text-black dark:text-white tracking-tighter">Conta Criada!</h2>
          <p className="text-neutral-500 dark:text-slate-400 font-medium text-sm leading-relaxed">
             {targetCabinetId 
                ? "Sua solicitação de acesso foi enviada ao administrador do gabinete. Aguarde a aprovação." 
                : "Seu perfil foi registrado. Verifique seu e-mail para confirmar a conta."}
          </p>
          <button onClick={onSwitchToLogin} className="w-full bg-black dark:bg-parliament-600 text-white rounded-2xl py-4 font-black text-xs uppercase tracking-widest shadow-lg hover:opacity-90 transition-all">Ir para Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-navy-950 flex items-center justify-center p-6 font-medium">
      <div className="w-full max-w-[420px] transition-all duration-500 animate-in slide-in-from-bottom-8">
        <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-[32px] p-10 shadow-2xl">
          <button onClick={onSwitchToLogin} className="self-start p-2 -ml-2 text-neutral-400 hover:text-black dark:hover:text-white transition-all mb-4"><ArrowLeft size={24} /></button>
          
          <div className="flex flex-col items-center text-center mb-10 relative">
            <h1 className="text-3xl font-black text-black dark:text-white tracking-tighter">Registrar Perfil</h1>
            <p className="text-neutral-400 dark:text-slate-500 mt-2 text-sm">Crie seu acesso ao Gabinete Digital</p>
            
            {targetCabinetName && (
               <div className="mt-4 px-4 py-2 bg-parliament-50 dark:bg-parliament-900/20 text-parliament-700 dark:text-parliament-300 rounded-xl font-bold text-xs flex items-center gap-2 border border-parliament-100 dark:border-parliament-800">
                  <Building2 size={14} /> Solicitando acesso a: {targetCabinetName}
               </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {errorMsg && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50 rounded-2xl text-red-600 dark:text-red-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                <AlertTriangle size={16} /> <span>{errorMsg}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-neutral-400 uppercase tracking-widest ml-4">Nome Completo</label>
              <div className="relative">
                <User className="absolute left-5 top-1/2 -translate-y-1/2 text-neutral-300 dark:text-navy-700" size={18} />
                <input required type="text" placeholder="Seu nome" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-neutral-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl pl-14 pr-6 py-4 text-sm font-bold focus:ring-2 focus:ring-black dark:focus:ring-parliament-600 outline-none transition-all dark:text-white" />
              </div>
            </div>
            
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-neutral-400 uppercase tracking-widest ml-4">E-mail</label>
              <div className="relative">
                <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-neutral-300 dark:text-navy-700" size={18} />
                <input required type="email" placeholder="nome@exemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-neutral-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl pl-14 pr-6 py-4 text-sm font-bold focus:ring-2 focus:ring-black dark:focus:ring-parliament-600 outline-none transition-all dark:text-white" />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-neutral-400 uppercase tracking-widest ml-4">Whatsapp (com DDD)</label>
              <div className="relative">
                <Phone className="absolute left-5 top-1/2 -translate-y-1/2 text-neutral-300 dark:text-navy-700" size={18} />
                <input required type="text" placeholder="(99) 99999-9999" value={phone} onChange={(e) => formatPhone(e.target.value)} className="w-full bg-neutral-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl pl-14 pr-6 py-4 text-sm font-bold focus:ring-2 focus:ring-black dark:focus:ring-parliament-600 outline-none transition-all dark:text-white" />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-neutral-400 uppercase tracking-widest ml-4">Senha</label>
              <div className="relative">
                <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-neutral-300 dark:text-navy-700" size={18} />
                <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-neutral-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800 rounded-2xl pl-14 pr-6 py-4 text-sm font-bold focus:ring-2 focus:ring-black dark:focus:ring-parliament-600 outline-none transition-all dark:text-white" />
              </div>
            </div>

            <button disabled={isSubmitting} type="submit" className="w-full bg-black dark:bg-parliament-600 text-white rounded-2xl py-4 font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-3 transition-all hover:scale-[1.01] active:scale-98 disabled:opacity-50">
              {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : 'FINALIZAR REGISTRO'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
